import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { EPCBadge } from "../components/EPCBadge";
import { PriorityBadge, PRIORITY_OPTIONS } from "../components/PriorityBadge";
import { Map as MapIcon, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SavedMapBatches, { BatchBadge } from "../components/SavedMapBatches";

const BAND_COLORS = {
  A: "#008054", B: "#19b459", C: "#8dce46", D: "#ffd500",
  E: "#fcaa65", F: "#ef8023", G: "#e9153b",
};
const PRIORITY_COLORS = {
  very_high: "#dc2626", high: "#ea580c", medium: "#f59e0b",
  low: "#86efac", minimal: "#15803d",
};

const BANDS = ["A", "B", "C", "D", "E", "F", "G"];
const UK_CENTRE = [52.6, 1.0]; // East Anglia centroid (matches Marks Sava stock)

function FitToBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (!markers.length) return;
    const lats = markers.map((m) => m.lat);
    const lngs = markers.map((m) => m.lng);
    const bounds = [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [markers, map]);
  return null;
}

export default function MapPage() {
  const [items, setItems] = useState([]);
  const [postcodeSummary, setPostcodeSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colorMode, setColorMode] = useState("priority"); // 'band' | 'priority'
  const [bandFilter, setBandFilter] = useState([]); // [] = all bands
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [cohortFilter, setCohortFilter] = useState("ALL");
  const [focusedPostcodes, setFocusedPostcodes] = useState(() => new Set()); // empty = all
  const [limit] = useState(1500);
  const [batches, setBatches] = useState([]);
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "manager", "editor"].includes(user?.role);

  useEffect(() => {
    api.get("/map-batches").then(({ data }) => setBatches(data.items)).catch(() => {});
  }, []);

  const activeBatch = useMemo(() => batches.find((b) =>
    b.postcodes.length === focusedPostcodes.size && b.postcodes.every((p) => focusedPostcodes.has(p))
  ), [batches, focusedPostcodes]);

  const batchesByPostcode = useMemo(() => {
    const idx = {};
    for (const b of batches) for (const p of b.postcodes) (idx[p] = idx[p] || []).push(b);
    return idx;
  }, [batches]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (bandFilter.length) params.bands = bandFilter.join(",");
      if (priorityFilter !== "ALL") params.priority = priorityFilter;
      if (cohortFilter !== "ALL") params.cohort = cohortFilter;
      const { data } = await api.get("/map/properties", { params });
      setItems(data.items);
      setPostcodeSummary(data.postcode_summary || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [bandFilter, priorityFilter, cohortFilter]); // eslint-disable-line

  const toggleBand = (b) => {
    setBandFilter((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));
  };

  // Multi-postcode selection helpers
  const togglePostcode = (pc) => {
    setFocusedPostcodes((prev) => {
      const next = new Set(prev);
      if (next.has(pc)) next.delete(pc);
      else next.add(pc);
      return next;
    });
  };
  // Group all postcodes by their outward part ("NR15 2XR" -> "NR15"). Click a
  // group chip to toggle every postcode that shares that prefix — same UX
  // pattern as Outlook's "select all in this folder".
  const postcodeGroups = useMemo(() => {
    const groups = {};
    for (const pc of postcodeSummary) {
      const outward = (pc.postcode || "").split(" ")[0];
      if (!outward) continue;
      (groups[outward] = groups[outward] || { outward, count: 0, postcodes: [] });
      groups[outward].count += pc.count;
      groups[outward].postcodes.push(pc.postcode);
    }
    // Sort by total properties desc
    return Object.values(groups).sort((a, b) => b.count - a.count);
  }, [postcodeSummary]);
  const toggleGroup = (group) => {
    setFocusedPostcodes((prev) => {
      const next = new Set(prev);
      const allSelected = group.postcodes.every((p) => next.has(p));
      if (allSelected) {
        for (const p of group.postcodes) next.delete(p);
      } else {
        for (const p of group.postcodes) next.add(p);
      }
      return next;
    });
  };

  // Pins visible right now (filtered by the postcode batch selector — empty
  // selection means show every loaded property).
  const visibleItems = useMemo(() => {
    if (focusedPostcodes.size === 0) return items;
    return items.filter((m) => focusedPostcodes.has(m.postcode));
  }, [items, focusedPostcodes]);

  // Furthest-pair distance across whatever postcodes are currently focused
  // (or every loaded pin when nothing is focused). Gives the operator a
  // sense of how compact a batch is (drive-time proxy).
  const focusedSpan = useMemo(() => {
    if (visibleItems.length < 2 || focusedPostcodes.size === 0) return null;
    // Haversine in metres — O(n²), capped at the first 500 pins so a big
    // multi-postcode batch doesn't lock the UI thread.
    const sample = visibleItems.slice(0, 500);
    const toRad = (d) => (d * Math.PI) / 180;
    const hav = (a, b) => {
      const R = 6371000;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    };
    let maxD = 0;
    for (let i = 0; i < sample.length; i += 1) {
      for (let j = i + 1; j < sample.length; j += 1) {
        const d = hav(sample[i], sample[j]);
        if (d > maxD) maxD = d;
      }
    }
    return maxD;
  }, [focusedPostcodes, visibleItems]);

  const colorFor = (m) => {
    if (colorMode === "band") return BAND_COLORS[m.band] || "#94a3b8";
    return PRIORITY_COLORS[m.priority] || "#94a3b8";
  };

  const legend = useMemo(() => {
    if (colorMode === "band") {
      return BANDS.map((b) => ({ label: `Band ${b}`, color: BAND_COLORS[b] }));
    }
    return [
      { label: "Very High", color: PRIORITY_COLORS.very_high },
      { label: "High", color: PRIORITY_COLORS.high },
      { label: "Medium", color: PRIORITY_COLORS.medium },
      { label: "Low", color: PRIORITY_COLORS.low },
      { label: "Minimal", color: PRIORITY_COLORS.minimal },
    ];
  }, [colorMode]);

  return (
    <div className="p-8 space-y-4 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Geo view</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1 flex items-center gap-3">
            <MapIcon className="h-7 w-7" /> Property Map
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Geocoded via <span className="font-mono">postcodes.io</span> · {items.length.toLocaleString("en-GB")} pins plotted
            {focusedPostcodes.size > 0 && (
              <span className="ml-2 font-bold text-slate-900">{activeBatch && <span data-testid="map-active-batch">· Batch #{activeBatch.number} “{activeBatch.name}” </span>}· batch-focused on {focusedPostcodes.size} postcode{focusedPostcodes.size === 1 ? "" : "s"} ({visibleItems.length})</span>
            )}
            <span className="ml-2 text-slate-500">(cap {limit.toLocaleString("en-GB")})</span>
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-none border-slate-300" data-testid="map-refresh">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Card className="rounded-none border-border p-4 flex flex-wrap items-center gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Colour by</div>
          <Tabs value={colorMode} onValueChange={setColorMode}>
            <TabsList className="rounded-none bg-slate-100 p-1 h-auto">
              <TabsTrigger value="priority" className="rounded-none data-[state=active]:bg-white" data-testid="map-color-priority">Retrofit Priority</TabsTrigger>
              <TabsTrigger value="band" className="rounded-none data-[state=active]:bg-white" data-testid="map-color-band">EPC Band</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
            Filter band {bandFilter.length > 0 && <span className="text-slate-500">· {bandFilter.length} selected</span>}
          </div>
          <div className="flex items-center gap-1" data-testid="map-filter-band-multi">
            {[...BANDS, "Unknown"].map((b) => {
              const active = bandFilter.includes(b);
              const baseColor = b === "Unknown" ? "#64748b" : BAND_COLORS[b];
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleBand(b)}
                  className="h-9 px-2.5 text-xs font-bold font-mono border transition-colors"
                  style={active
                    ? { backgroundColor: baseColor, color: "white", borderColor: baseColor }
                    : { backgroundColor: "white", color: baseColor, borderColor: "#cbd5e1" }}
                  data-testid={`map-band-toggle-${b}`}
                  title={`Toggle band ${b}`}
                >
                  {b}
                </button>
              );
            })}
            {bandFilter.length > 0 && (
              <button
                type="button"
                onClick={() => setBandFilter([])}
                className="h-9 px-2 text-[10px] underline text-slate-600 hover:text-slate-900"
                data-testid="map-band-toggle-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Filter priority</div>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-52 rounded-none border-slate-300 h-9" data-testid="map-filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All priorities</SelectItem>
              {PRIORITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Filter cohort</div>
          <Select value={cohortFilter} onValueChange={setCohortFilter}>
            <SelectTrigger className="w-52 rounded-none border-slate-300 h-9" data-testid="map-filter-cohort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All cohorts</SelectItem>
              <SelectItem value="none">Backlog (no programme)</SelectItem>
              <SelectItem value="wave3-y1">Wave 3 · Y1 (delivered)</SelectItem>
              <SelectItem value="wave3-y2">Wave 3 · Y2 (scheduled)</SelectItem>
              <SelectItem value="wave3-y2-standby">Wave 3 · Y2 Standby</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 flex flex-wrap justify-end gap-3">
          {legend.map((l) => (
            <div key={l.label} className="flex items-center gap-1.5 text-xs">
              <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: l.color }} />
              <span className="font-mono">{l.label}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Postcode-batch panel — for visit-planning. Click any postcode chip
          to toggle it into the focus set, or click a group chip (e.g. "NR15")
          to toggle every postcode that starts with that outward code at once. */}
      <Card className="rounded-none border-border" data-testid="postcode-batches">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
              Postcode batches — densest first
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {focusedPostcodes.size > 0 ? (
                <>
                  Focused: <span className="font-mono font-bold">{focusedPostcodes.size}</span> postcode{focusedPostcodes.size === 1 ? "" : "s"} · {visibleItems.length} {visibleItems.length === 1 ? "property" : "properties"}
                  {focusedSpan != null && (
                    <span className="ml-2 text-slate-500">· max span <span className="font-mono">{focusedSpan < 1000 ? `${Math.round(focusedSpan)} m` : `${(focusedSpan / 1000).toFixed(2)} km`}</span> across the batch</span>
                  )}
                  <button onClick={() => setFocusedPostcodes(new Set())} className="ml-3 text-[10px] underline text-slate-700 hover:text-slate-900" data-testid="clear-postcode-focus">
                    Clear all
                  </button>
                </>
              ) : (
                <>{postcodeSummary.length} unique postcodes in <span className="font-bold">{postcodeGroups.length}</span> outward group{postcodeGroups.length === 1 ? "" : "s"} — click an <span className="font-mono">NR15…</span> group to batch every postcode at once, or pick individual postcodes below.</>
              )}
            </div>
          </div>
        </div>
        <SavedMapBatches
          batches={batches}
          setBatches={setBatches}
          activeBatchId={activeBatch?.id}
          focusedPostcodes={focusedPostcodes}
          canEdit={canEdit}
          onLoad={(b) => setFocusedPostcodes(new Set(b.postcodes))}
        />
        {/* Outward-code group chips — bulk-select every postcode sharing the prefix */}
        {postcodeGroups.length > 1 && (
          <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap gap-2 items-center" data-testid="postcode-groups">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mr-1">Groups</span>
            {postcodeGroups.slice(0, 30).map((g) => {
              const selectedInGroup = g.postcodes.filter((p) => focusedPostcodes.has(p)).length;
              const allSelected = selectedInGroup === g.postcodes.length && selectedInGroup > 0;
              const someSelected = selectedInGroup > 0 && !allSelected;
              return (
                <button
                  key={g.outward}
                  type="button"
                  onClick={() => toggleGroup(g)}
                  className="px-2.5 py-1 text-[11px] font-mono border-2 whitespace-nowrap transition-colors"
                  style={allSelected
                    ? { backgroundColor: "var(--hi-olive)", color: "white", borderColor: "var(--hi-olive)" }
                    : someSelected
                    ? { backgroundColor: "var(--hi-cream)", color: "var(--hi-olive)", borderColor: "var(--hi-olive)" }
                    : { backgroundColor: "white", color: "var(--hi-olive)", borderColor: "#cbd5e1" }}
                  data-testid={`postcode-group-${g.outward}`}
                  title={`${g.count} properties across ${g.postcodes.length} postcodes starting with ${g.outward}`}
                >
                  {g.outward}…
                  <span className="opacity-70 ml-1">{g.postcodes.length}pc · ×{g.count}</span>
                  {someSelected && <span className="ml-1 text-[9px]">({selectedInGroup}/{g.postcodes.length})</span>}
                </button>
              );
            })}
            {postcodeGroups.length > 30 && (
              <span className="text-[11px] text-slate-500 self-center">…+{postcodeGroups.length - 30} more groups</span>
            )}
          </div>
        )}
        {/* Individual postcode chips */}
        <div className="px-4 py-3 max-h-40 overflow-y-auto flex flex-wrap gap-2">
          {postcodeSummary.slice(0, 120).map((pc) => {
            const isFocused = focusedPostcodes.has(pc.postcode);
            return (
              <button
                key={pc.postcode}
                type="button"
                onClick={() => togglePostcode(pc.postcode)}
                className="px-2 py-1 text-[11px] font-mono border whitespace-nowrap transition-colors"
                style={isFocused
                  ? { backgroundColor: "var(--hi-orange-deep)", color: "white", borderColor: "var(--hi-orange-deep)" }
                  : { backgroundColor: "white", color: "var(--hi-orange-deep)", borderColor: "#e2e8f0" }}
                data-testid={`postcode-chip-${pc.postcode.replace(/\s/g, "")}`}
                title={`${pc.count} ${pc.count === 1 ? "property" : "properties"} at ${pc.postcode}`}
              >
                {pc.postcode} <span className="opacity-60 ml-1">×{pc.count}</span>
                {(batchesByPostcode[pc.postcode] || []).some((b) => b.starred) && <span className="ml-1 text-amber-500">★</span>}
              </button>
            );
          })}
          {postcodeSummary.length > 120 && (
            <span className="text-[11px] text-slate-500 self-center">…+{postcodeSummary.length - 120} more</span>
          )}
        </div>
      </Card>

      <Card className="rounded-none border-border overflow-hidden" data-testid="map-card">
        <div className="h-[68vh] relative">
          {loading && (
            <div className="absolute inset-0 bg-white/70 z-[400] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-700" />
            </div>
          )}
          <MapContainer center={UK_CENTRE} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitToBounds markers={visibleItems} />
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={60}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
              iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();
                const size = count < 25 ? 36 : count < 100 ? 44 : count < 500 ? 52 : 60;
                const bg = count < 25 ? "#0f172a" : count < 100 ? "#1e293b" : count < 500 ? "#b45309" : "#dc2626";
                return L.divIcon({
                  html: `<div style="background:${bg};color:#fff;width:${size}px;height:${size}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-family:'IBM Plex Mono',monospace;font-weight:700;font-size:${count > 999 ? 11 : 13}px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${count.toLocaleString("en-GB")}</div>`,
                  className: "epc-cluster-icon",
                  iconSize: L.point(size, size, true),
                });
              }}
            >
              {visibleItems.map((m) => (
                <CircleMarker
                  key={m.id}
                  center={[m.lat, m.lng]}
                  radius={focusedPostcodes.size > 0 ? 9 : 7}
                  pathOptions={{
                    color: colorFor(m),
                    fillColor: colorFor(m),
                    fillOpacity: 0.85,
                    weight: focusedPostcodes.size > 0 ? 2 : 1.5,
                  }}
                >
                  <Popup>
                    <div className="text-sm" style={{ fontFamily: "IBM Plex Sans, system-ui" }}>
                      <div className="font-bold">{m.address}</div>
                      <div className="text-xs text-slate-500 font-mono">{m.postcode}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <EPCBadge band={m.band} size="sm" />
                        <PriorityBadge priority={m.priority} score={m.score} size="sm" />
                      </div>
                      {m.cohort && (
                        <div className="text-[10px] font-mono mt-1.5 uppercase tracking-wider"
                             style={{ color: m.cohort === "wave3-y1" ? "#065f46" : m.cohort === "wave3-y2" ? "#0c4a6e" : "#5b21b6" }}>
                          {m.cohort === "wave3-y1" ? "✓ Y1 delivered" : m.cohort === "wave3-y2" ? "★ Y2 scheduled" : "○ Y2 standby"}
                        </div>
                      )}
                      {batchesByPostcode[m.postcode] && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {batchesByPostcode[m.postcode].map((b) => <BatchBadge key={b.id} batch={b} />)}
                        </div>
                      )}
                      {m.survey_state === "attempted_no_contact" && (
                        <div className="text-[10px] font-mono mt-1 text-amber-700 uppercase tracking-wider">⚠ no contact</div>
                      )}
                      {m.property_type && <div className="text-xs mt-2 text-slate-600">{m.property_type}</div>}
                      {m.sap && <div className="text-xs font-mono text-slate-500">SAP {m.sap}</div>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      </Card>

      {loading && items.length === 0 && (
        <Skeleton className="h-6 w-48" />
      )}
    </div>
  );
}
